import React, { useEffect, useMemo, useRef, useState } from 'react';
import { xlate, xlationContext } from '../../../../framework/utils/i18n';
import { IStyle, mergeStyles, mergeStyleSets, Text } from '@fluentui/react';
import * as formService from '../../form-services/presentation-editor';
import { isNotNullOrWhitespace } from '../../../../framework/utils';
import { secondsToDurationString } from '../../../utils';
import { getUtcNowUnixTimestamp } from '../../../../framework/utils/date-time';
import { BwjPalette } from '../../../app-theme';
import { TimingState } from '../../../../entities/timing-state';
import { PresentationTiming } from '../../../../entities-generated/common/presentation-timing';
import { PRIVATE_TIME_MONITOR_HEADER_END_TIME_DISPLAY } from './end-time';
import { IPresenterAccountPreferences } from '../../../../entities-generated/common/presenter-account-preferences';
import { PRIVATE_TIME_MONITOR_HEADER_EXPANDER } from './expander';

xlationContext('Presentation time monitor header');

interface ITimingMonitorHeaderProps {
  accountSettings: IPresenterAccountPreferences;
  timeAllotted_ms: number;
  projectedTimeDelta_seconds: number | undefined;
  warningColorThreshold: number;
  onTimingStateChanged: (state: TimingState) => void;
  onOvertimeTick: () => void;
}

const getHeaderClass = (backgroundColor: string) => {
  return mergeStyles({
    padding: 20,
    position: 'sticky',
    top: 0,
    left: 0,
    backgroundColor,
    color: 'white',
    zIndex: 10,
  });
}

const getStyleClasses = () => {
  const topRow: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
    fontSize: 16,
  };

  const title: IStyle = {
    margin: 0,
    fontSize: 18,
    fontWeight: 400,
    width: 1,
    flexGrow: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const timeStatusRow: IStyle = {
    marginTop: 20,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };

  const countdown: IStyle = {
    paddingBottom: 5,
    margin: 0
  };

  return mergeStyleSets({
    topRow,
    title,
    timeStatusRow,
    countdown,
  });
}

const subscriberId = 'Component.TimingMonitorHeader';

export const TimingMonitorHeader: React.FunctionComponent<ITimingMonitorHeaderProps> = (props) => {
  const { timeAllotted_ms, projectedTimeDelta_seconds, warningColorThreshold, onTimingStateChanged, onOvertimeTick } = props;
  const formModel = formService.useFormModel(subscriberId);
  const { timing: modelTiming, currentSectionStart } = formModel;
  const [timeRemaining, setTimeRemaining] = useState(timeAllotted_ms);
  const [headerExpanded, setHeaderExpanded] = useState(true);
  const prevTimingState = useRef(TimingState.Active);
  const styles = getStyleClasses();

  const subtitle = isNotNullOrWhitespace(formModel.currentSection.title) ? formModel.currentSection.title : undefined;
  const title = subtitle === undefined ? formModel.title.value
  : `${formModel.title.value} / ${subtitle}`;

  const timeCaption = useMemo(() => {
    if(projectedTimeDelta_seconds !== undefined && timeRemaining < 0) {
      return xlate('remaining ({sign}{delta})', { delta: secondsToDurationString(Math.abs(projectedTimeDelta_seconds)), sign: projectedTimeDelta_seconds < 0 ? '-' : '+'});
    }
    return timeRemaining < 0 ? xlate('overtime') : xlate('remaining');
  }, [projectedTimeDelta_seconds, timeRemaining]);

  const timeDisplay = useMemo(() => {
    const totalSecondsRemaining = Math.round(Math.abs(timeRemaining) / 1000);
    return secondsToDurationString(totalSecondsRemaining);
  }, [timeRemaining]);

  const backgroundColor = useMemo(() => {
    const warningThreshold = timeAllotted_ms * warningColorThreshold;
    if(timeRemaining > warningThreshold) {
      return BwjPalette.themeSuccess;
    }
    if(timeRemaining >= 0) {
      return BwjPalette.themeWarning;
    }

    return BwjPalette.themeError;
  }, [timeAllotted_ms, timeRemaining, warningColorThreshold]);

  useEffect(() => {
    let timingState = TimingState.Active;
    const warningThreshold = timeAllotted_ms * warningColorThreshold;

    if(timeRemaining <= 0) {
      timingState = TimingState.Overtime;
    } else if (timeRemaining < warningThreshold) {
      timingState = TimingState.Warning;
    }

    if(prevTimingState.current !== timingState) {
      prevTimingState.current = timingState;
      onTimingStateChanged(timingState);
    }
  }, [timeAllotted_ms, timeRemaining, warningColorThreshold, onTimingStateChanged]);

  useEffect(() => {
    prevTimingState.current = TimingState.Active;

    const onInterval = (firstInvocation: boolean = false) => {
      const timeElasped = getUtcNowUnixTimestamp() - currentSectionStart;
      const timeDelta = timeAllotted_ms - timeElasped;
      setTimeRemaining(timeDelta);
      if(firstInvocation === false) {
        if((modelTiming === PresentationTiming.Flexible || modelTiming === PresentationTiming.Proportional)
        && timeDelta < 0) {
          onOvertimeTick();
        }
      }
    };

    onInterval(true);
    const cancelInterval = window.setInterval(onInterval, 500);

    return () => {
      window.clearInterval(cancelInterval);
    };
  }, [timeAllotted_ms, modelTiming, currentSectionStart, onOvertimeTick]);

  return (
    <header className={getHeaderClass(backgroundColor)}>
      <div className={styles.topRow}>
        <h1 className={styles.title}>{title}</h1>
        <PRIVATE_TIME_MONITOR_HEADER_END_TIME_DISPLAY
          accountSettings={props.accountSettings}
          endTime={formModel.endTime}
        />
        <PRIVATE_TIME_MONITOR_HEADER_EXPANDER
          headerExpanded={headerExpanded}
          onClicked={() => setHeaderExpanded(!headerExpanded)}
        />
      </div>
      {headerExpanded &&
      <div className={styles.timeStatusRow}>
        <h2 className={styles.countdown}>
          <Text variant={'mega'} nowrap>{timeDisplay}</Text>
        </h2>
        <h3 style={{ margin: 0}}>
          <Text variant={'medium'} nowrap>{timeCaption}</Text>
        </h3>
      </div>}
    </header>
  );
};
